"use client";

import { differenceInHours, format } from "date-fns";
import { useEffect, useState } from "react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "../ui";
import { DateRangePicker } from "./date-range-picker";
import type { DatePreset, RelativeDateRange } from "~/types/ui";
import { convertRelativeDateRange, isRelativeTime } from "~/utils/date-util";

export const DatePresetHours: Record<DatePreset, number> = {
  "1h": 1,
  "6h": 6,
  "12h": 12,
  "1d": 24,
  "7d": 24 * 7,
  "30d": 24 * 30,
};

export function DatePresetPicker({
  range,
  setRange,
}: {
  range: RelativeDateRange;
  setRange: (date: RelativeDateRange) => void;
}) {
  const [selectedPreset, setSelectedPreset] = useState<DatePreset | null>("1d");

  useEffect(() => {
    const dr = convertRelativeDateRange(range);

    if (dr?.from) {
      const hoursSince = differenceInHours(dr.to, dr.from);
      const matchedPreset = (Object.keys(DatePresetHours) as DatePreset[]).find(
        (preset) => DatePresetHours[preset] === hoursSince,
      );
      if (matchedPreset && matchedPreset !== selectedPreset) {
        setSelectedPreset(matchedPreset);
      } else if (!matchedPreset && selectedPreset) {
        setSelectedPreset(null);
      }
    }
  }, [range]);

  const presets = (Object.keys(DatePresetHours) as DatePreset[]).map(
    (preset, i) => {
      return (
        <Button
          type="button"
          variant={selectedPreset === preset ? "default" : "outline"}
          key={preset}
          // Selection was conveyed by background colour alone, so a screen reader user
          // got six identical buttons with no indication of which range was active.
          // The colour itself now comes from `variant` above rather than from explicit
          // bg classes, so only the announcement is added here.
          aria-pressed={selectedPreset === preset}
          className={`px-2 py-1 ${i === 0 ? "rounded-none rounded-l-md" : "rounded-none"} text-sm`}
          onClick={() => {
            setSelectedPreset(preset);
            setRange({ from: `-${preset}`, to: "Now" });
          }}
        >
          {preset}
        </Button>
      );
    },
  );
  const [open, setOpen] = useState(false);

  return (
    <div>
      {presets}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant={selectedPreset === null ? "default" : "outline"}
            key="range"
            aria-pressed={selectedPreset === null}
            className="py-1 rounded-none rounded-r-md text-sm"
            onClick={() => {
              setOpen(true);
            }}
          >
            {range?.from === undefined || isRelativeTime(range?.from)
              ? (range?.from ?? "")
              : format(new Date(range.from), "yyyy/MM/dd HH:mm")}{" "}
            to{" "}
            {range?.to === undefined || isRelativeTime(range?.to)
              ? (range?.to ?? "")
              : format(new Date(range.to), "yyyy/MM/dd HH:mm")}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-full overflow-scroll no-scrollbar max-h-[var(--radix-popover-content-available-height)] p-0 m-0"
          onWheel={(e) => e.stopPropagation()}
        >
          <DateRangePicker
            value={range}
            setRange={setRange}
            onClose={() => setOpen(false)}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
